// Упражнение дома и во дворе (К-12, FR-HOME-02/03): схема на доске с анимацией, шаги, безопасность, «Сделали!».
import { useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { Board, buildStep, piecesFromScene, staticPose, usePlayback, type BoardHandle, type Point } from "@brovka/board";
import type { ExerciseScheme, StepTimeline } from "@brovka/content-types";
import { TopBar } from "../components/Shell.tsx";
import { findExercise } from "../lib/content/catalog.ts";
import { record } from "../lib/events.ts";
import { activeChild, useApp } from "../state/app.ts";
import { t } from "../i18n/ru.ts";

function schemeSteps(sc: ExerciseScheme): StepTimeline[] {
  const field = { lengthM: sc.lengthM, widthM: sc.widthM };
  let pos: Record<string, Point> = {};
  let owner: string | null = null;
  for (const o of sc.scene.objects) {
    if (o.type === "ball") owner = o.owner ?? null;
    else if ("pos" in o) pos[o.id] = o.pos as Point;
  }
  return sc.steps.map((st) => {
    const moves = st.moves.map((m) =>
      m.type === "run" || m.type === "dribble"
        ? { kind: m.type, obj: m.obj, to: m.to as Point }
        : m.type === "pass"
          ? { kind: "pass" as const, to: typeof m.to === "string" ? m.to : (m.to as Point) }
          : m.type === "tackle"
            ? { kind: "tackle" as const, by: m.by }
            : { kind: "pass" as const, to: [100, 50] as Point },
    );
    const step = buildStep(pos, owner, moves, field);
    pos = { ...(step.end as Record<string, Point>) };
    owner = step.ownerEnd ?? null;
    return step;
  });
}

function Scheme({ sc }: { sc: ExerciseScheme }) {
  const board = useRef<BoardHandle>(null);
  const steps = useMemo(() => schemeSteps(sc), [sc]);
  const pb = usePlayback(steps, board, { autoplay: false });
  const pieces = useMemo(() => piecesFromScene(sc.scene), [sc.scene]);
  const first = steps[0];
  const pose = useMemo(() => staticPose(first?.start ?? {}, first?.ownerStart), [first]);
  return (
    <div>
      <div className="board-box">
        <Board ref={board} field={{ lengthM: sc.lengthM, widthM: sc.widthM, goals: [] }} pieces={pieces} pose={pose} arrows={steps[pb.step]?.segments} ariaLabel={sc.steps.map((s) => s.text).join(" ")} />
      </div>
      <div className="playbar">
        <button className="icon-btn icon-btn--primary" onClick={pb.playing ? pb.pause : pb.play} aria-label={pb.playing ? t.player.pause : t.player.play}>
          {pb.playing ? "❚❚" : "▶"}
        </button>
        <span>{sc.steps[pb.step]?.text}</span>
      </div>
    </div>
  );
}

export function ExerciseScreen() {
  const { id = "" } = useParams();
  const nav = useNavigate();
  const content = useApp((s) => s.content);
  const e = content ? findExercise(content, id) : undefined;
  const [done, setDone] = useState(false);
  if (!content || !e) return <p className="page">{t.player.notFound}</p>;
  const principles = e.principles.map((p) => content.dictionaries.principles[p]?.name ?? p).join(", ");
  const list = (title: string, items: string[]) =>
    items.length ? (
      <section>
        <h2>{title}</h2>
        <ul>{items.map((x) => <li key={x}>{x}</li>)}</ul>
      </section>
    ) : null;
  return (
    <div className="page exercise">
      <TopBar title={e.title} back={() => nav(-1)} />
      <dl className="facts">
        <dt>{t.exercise.goal}</dt>
        <dd>{principles}</dd>
        <dt>{t.exercise.equipment}</dt>
        <dd>{e.equipment.join(", ")}</dd>
        <dt>{t.exercise.area}</dt>
        <dd>
          {e.area} · {e.conditions.map((c) => t.exercise.conditions[c]).join(", ")}
        </dd>
        <dt>{t.exercise.participants}</dt>
        <dd>{e.participants}</dd>
        <dt>{t.exercise.duration}</dt>
        <dd>{t.common.minutes(e.durationMin)}</dd>
      </dl>
      {e.scheme ? <Scheme sc={e.scheme} /> : null}
      <section>
        <h2>{t.exercise.steps}</h2>
        <ol>{e.steps.map((s) => <li key={s}>{s}</li>)}</ol>
      </section>
      {list(t.exercise.harder, e.harder)}
      {list(t.exercise.easier, e.easier)}
      <div className="callout">{list(t.exercise.safety, e.safety)}</div>
      {done ? (
        <p className="callout callout--good">{t.exercise.doneThanks}</p>
      ) : (
        <button
          className="btn btn--primary btn--big"
          onClick={() => {
            const child = activeChild();
            if (child) void record(child.id, "home_exercise_done", { exercise_id: e.id, principles: e.principles });
            setDone(true);
          }}
        >
          ✔ {t.exercise.done}
        </button>
      )}
    </div>
  );
}
